import {NgModule} from '@angular/core';
import {Routes, RouterModule} from '@angular/router';
import {BrowserUtils} from '@azure/msal-browser';
import {HomeComponent} from './home/home.component';
import {FailedComponent} from './failed/failed.component';
import {MsalGuard} from '@azure/msal-angular';
import {organizationAccessGuard} from './guards/organization-access.guard';
import {permissionsAdminGuard} from './guards/permissions-admin.guard';
import {pagePermissionGuard} from './guards/page-permission.guard';
import {unsavedChangesGuard} from './guards/unsaved-changes.guard';
import {customersMsalGuard} from './auth/msal/guards/customers-msal.guard';
import {NotificationsPageComponent} from './notifications/notifications-page/notifications-page.component';

const routes: Routes = [
  {
    path: '',
    component: HomeComponent,
    canActivate: [MsalGuard]
  },
  {
    path: 'onboarding/company',
    loadComponent: () => import('./onboarding-company/onboarding-company.component')
      .then(x => x.OnboardingCompanyComponent),
    canActivate: [MsalGuard, organizationAccessGuard],
    // data: {
    //   permissions: {
    //     only: ['OnboardingAdmin']
    //   }
    // }
  },
  {
    path: 'settings/user',
    loadComponent: () => import('./user-settings/user-settings-wrapper.component')
      .then(x => x.UserSettingsWrapperComponent),
    canActivate: [MsalGuard, organizationAccessGuard]
  },
  {
    path: 'settings/organization',
    loadComponent: () => import('./organization-settings/organization-settings.component')
      .then(x => x.OrganizationSettingsComponent),
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    data: { pageKey: 'corporate.organization-settings' }
  },
  {
    path: 'settings/permissions',
    loadComponent: () => import('./permissions/admin/permissions-admin-page.component')
      .then(x => x.PermissionsAdminPageComponent),
    canActivate: [MsalGuard, organizationAccessGuard, permissionsAdminGuard],
  },
  // {
  //   path: 'settings/company/:companyId',
  //   component: CompanyManagementComponent,
  //   canActivate: [MsalGuard, ngxPermissionsGuard, organizationAccessGuard],
  //   data: {
  //     permissions: {
  //       only: ['OnboardingAdmin']
  //     }
  //   }
  // },
  {
    path: 'settings/company/:companyId',
    pathMatch: 'full',
    loadComponent: () => import('./organization-settings/organization-company-settings/organization-settings-wrapper.component')
      .then((x) => x.OrganizationSettingsComponent),
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    data: { pageKey: 'corporate.company-settings' }
  },
  {
    path: 'settings/callback/:companyId',
    pathMatch: 'full',
    loadComponent: () => import('./organization-settings/organization-company-settings/organization-settings-create-wrapper')
      .then((x) => x.SettingsCreateWrapperComponent),
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    data: { pageKey: 'corporate.company-settings-callback' }
  },
  {
    path: 'create-form/2',
    canActivate: [MsalGuard, organizationAccessGuard],
    canDeactivate: [unsavedChangesGuard],
    loadComponent: () => import('./corporate/introducer-create/introducer-create-wrapper.component')
      .then(x => x.IntroducerCreateWrapperComponent),
  },
  {
    path: 'create-form/:id',
    loadComponent: () => import('./corporate/contact/contact-wrapper.component')
      .then(x => x.ContactWrapperComponent),
    pathMatch: 'full',
    canActivate: [MsalGuard, organizationAccessGuard],
    canDeactivate: [unsavedChangesGuard],
  },

  {
    path: 'employee/:id',
    loadChildren: () => import('./company-management/employee-dashboard/employee-dashboard-routing.module').then(m => m.EmployeeDashboardRoutingModule)
  },

  {
    path: 'forms-table',
    loadComponent: () => import('./corporate/form-table/form-table-wrapper.component')
      .then(x => x.FormTableWrapperComponent),
    canActivate: [MsalGuard, organizationAccessGuard],
  },
  {
    path: 'edit-introducer/:id',
    canActivate: [MsalGuard, organizationAccessGuard],
    canDeactivate: [unsavedChangesGuard],
    loadComponent: () => import('./corporate/introducer-create/introducer-create-wrapper.component')
      .then(x => x.IntroducerCreateWrapperComponent),
  },
  {
    path: 'edit-form/:id',
    loadComponent: () => import('./corporate/edit-form/edit-form-wrapper.component')
      .then(x => x.EditFormWrapperComponent),
    canActivate: [MsalGuard, organizationAccessGuard],
    canDeactivate: [unsavedChangesGuard]
  },
  {
    path: 'form-constructor',
    loadComponent: () => import('./corporate/form-constructor/form-constructor-wrapper.component')
      .then(x => x.FormConstructorWrapperComponent),
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    canDeactivate: [unsavedChangesGuard],
    data: { pageKey: 'corporate.form-constructor' }
  },
  {
    path: 'corporate/risk-settings',
    loadComponent: () => import('./company-management/risk-assessment-settings/risk-assessment-settings-wrapper.component')
      .then(x => x.RiskAssessmentSettingsWrapperComponent),
    canActivate: [MsalGuard, pagePermissionGuard],
    data: { pageKey: 'corporate.risk-settings' }
  },
  {
    path: 'risk-assessment/answers/:formId/:submittedFormId/:id/:businessId',
    loadComponent: () => import('./corporate/risk-assesment/components/risk-stepper/risk-stepper.component')
      .then(x => x.RiskStepperComponent),
    pathMatch: 'full',
  },
  {
    path: 'paf-Document',
    loadComponent: () => import('./corporate/paf-document/paf-document.component')
      .then(x => x.PafDocumentComponent),
    pathMatch: 'full',
  },

  {
    path: 'time-sheet',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./time-sheet/time-sheet-wrapper.component')
      .then(x => x.TimeSheetWrapperComponent)
  },
  {
    path: 'profitability-report',
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/profitability-report/profitability-report.component')
      .then(x => x.ProfitabilityReportComponent),
    data: { pageKey: 'corporate.profitability-report' }
  },
  {
    path: 'log-changes',
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    loadComponent: () => import('./log-changes/log-changes.component')
      .then(x => x.LogChangesComponent),
    data: { pageKey: 'corporate.log-changes' }
  },
  {
    path: 'admin/pending-changes',
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/pending-changes/pending-changes-page.component')
      .then(x => x.PendingChangesPageComponent),
    data: { pageKey: 'corporate.pending-changes' }
  },
  {
    path: 'audit-logs',
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    loadComponent: () => import('./audit-logs/audit-logs.component')
      .then(x => x.AuditLogsComponent),
    data: { pageKey: 'corporate.audit-logs' }
  },
  {
    path: 'reports',
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/corporate-reports/corporate-reports.component')
      .then(x => x.CorporateReportsComponent),
    data: { pageKey: 'corporate.reports' }
  },

  {
    path: 'invoicing',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadChildren: () => import('./invoicing-module/invoicing-module-routing/invoicing.module').then(x => x.InvoicingModule)
  },
  {
    path: 'global-requests',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadChildren: () => import('./global-requests/global-requests.module').then(x => x.GlobalRequestsModule)
  },
  {
    path: 'invoicing-dashboard',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./invoicing-module/components/dashboard/invoicing-dashboard.component')
      .then(x => x.InvoicingDashboardComponent),
  },
  {
    path: 'pluton-invoicing',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadChildren: () => import('./pluton-invoicing-module/invoicing-module-routing/pluton-invoicing.module').then(x => x.PlutonInvoicingModule)
  },
  {
    path: 'pluton-invoicing-dashboard',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./pluton-invoicing-module/components/dashboard/invoicing-dashboard.component')
      .then(x => x.PlutonInvoicingDashboardComponent),
  },
  {
    path: 'login-failed',
    component: FailedComponent
  },
  {
    path: 'important-dates',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./important-dates/important-dates.component')
      .then(x => x.ImportantDatesComponent),
  },
  {
    path: 'auth/form/:id',
    canActivate: [customersMsalGuard],
    loadComponent: () => import('./auth/auth-onboard-form/auth-onboard-form.component')
      .then(x => x.AuthOnboardFormComponent),
    pathMatch: 'full'
  },
  {
    path: 'client-referral-agreement',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/client-referral-agreement/client-referral-agreement.component')
      .then(x => x.ClientReferralAgreementComponent),
  },
  {
    path: 'nda-agreement',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/nda-agreement/nda-agreement.component')
      .then(x => x.NdaAgreementComponent),
  },
  {
    path: 'engagement-agreement',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/engagement-agreement/engagement-agreement.component')
      .then(x => x.EngagementAgreementComponent),
  },
  {
    path: 'ariadni-cyl1',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/ariadni-cyl1/ariadni-cyl1.component')
      .then(x => x.AriadniCyl1Component),
  },
  {
    path: 'e-codes-application',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/e-codes-application/e-codes-application.component')
      .then(x => x.ECodesApplicationComponent),
  },
  {
    path: 'auth-representatives',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/auth-representatives/auth-representatives.component')
      .then(x => x.AuthRepresentativesComponent),
  },
  {
    path: 'authorization-form',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/authorization-form/authorization-form.component')
      .then(x => x.AuthorizationFormComponent),
  },
  {
    path: 'm121-english',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/m121-english/m121-english.component')
      .then(x => x.M121EnglishComponent),
  },
  {
    path: 'm121-greek',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/m121-greek/m121-greek.component')
      .then(x => x.M121GreekComponent),
  },
  {
    path: 'dividends-certificate',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/dividends-certificate/dividends-certificate.component')
      .then(x => x.DividendsCertificateComponent),
  },
  {
    path: 'file-system',
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    loadComponent: () =>  import('./file-storage/file-storage.component')
      .then(x => x.FileStorageComponent),
    data: { pageKey: 'corporate.file-system' }
  },
  {
    path: 'share-form',
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    loadComponent: () =>  import('./sharing-forms/components/sharing-forms-table/sharing-forms-table.component')
      .then(x => x.SharingFormsTableComponent),
    data: { pageKey: 'corporate.share-form' }
  },
  {
    path: 'task-planner',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () =>  import('./corporate/user-planer/user-planer.component')
      .then(x => x.UserPlanerComponent),
    // data: {
    //   permissions: {
    //     only: ['CorporateAdmin']
    //   }
    // }
  },
  {
    path: 'reference-numbers',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/reference-numbers/reference-numbers.component')
      .then(x => x.ReferenceNumbersComponent),
  },
  {
    path: 'company-shares',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/company-shares/company-shares.component')
      .then(x => x.CompanySharesComponent),
  },
  {
    path: 'phonebook',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/phonebook/components/phonebook-page/phonebook-page.component')
      .then((x) => x.PhonebookPageComponent)
  },
  {
    path: 'meeting-note',
    loadComponent: () => import('./corporate/meeting-notes/meeting-note-full/meeting-note-full.component')
      .then((x) => x.MeetingNoteFullComponent)
  },
  {
    path: 'tagged-notes',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/meeting-notes/tagged-notes-page/tagged-notes-page.component')
      .then((x) => x.TaggedNotesPageComponent)
  },
  {
    path: 'auth/success',
    canActivate: [customersMsalGuard],
    loadComponent: () => import('./auth/auth-success/auth-success.component')
      .then(x => x.AuthSuccessComponent)
  },
  {
    path: 'notifications',
    component: NotificationsPageComponent,
    canActivate: [MsalGuard, organizationAccessGuard]
  },
  {
    path: '**',
    redirectTo: '/'
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    // Don't perform initial navigation in iframes or popups
    initialNavigation: !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup() ? 'enabledNonBlocking' : 'disabled' // Set to enabledBlocking to use Angular Universal
  })],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
